import * as React from 'react';

import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { VariableSizeGrid, VariableSizeList } from 'react-window';
import { ReactWindowScroller } from 'react-window-scroller';

import { useMedia, useRect } from '~shared/hooks';
import { countDecimalPlaces } from '~shared/utils/number';
import {
  Direction,
  sortItemsByProperty,
  sortItemsByReferenceOrder,
} from '~shared/utils/sort';
import { ScoreDisplay } from '~teacher/components/ResultsGrid/types/ScoreDisplay';

import { calculatePercent } from '../../../util/math';
import { useResultState } from '../ActivityResult/ActivityResultProvider';
import { useStudentSortProvider } from '../ActivityResult/context/StudentSortContext';
import { useStudentScoreProvider } from '../StudentScore';
import { findStudentScore } from '../StudentScore/utils';
import { ResultsFooterCell } from './ResultsFooterCell';
import { ResultsFooterHeader } from './ResultsFooterHeader';
import { ResultsParticipantCell } from './ResultsParticipantCell';
import { ResultsParticipantHeader } from './ResultsParticipantHeader';
import { ResultsQuestionCell } from './ResultsQuestionCell';
import { ResultsResponseCell } from './ResultsResponseCell';
import { StudentTimerDisplayState } from './useStudentTimerStates';

export type ResultsTableQuestion = {
  questionId: number;
  order: number;
  type: QuestionTypes;
  path: string;
  hasCorrectAnswer: boolean;
  gradingWeight: number;
  totalGrades: number | null;
  totalParticipants: number;
};

export type StudentResponse = {
  id: number;
  questionId: number;
  userUuid: string;
  isCorrect: boolean | null | undefined;
  hidden: boolean;
  answerText: string;
  timestamp?: Date;
};

export type Participant = {
  id: number | string;
  uuid: string;
  here: boolean;
  name: string;
  finished: boolean;
  currentGrade: number;
  questionsAnswered: number;
  responsesByQuestion: Record<string, StudentResponse[]>;
};

interface Props {
  showNames: boolean;
  showResults: boolean;
  showResponses: boolean;
  hasTimers?: boolean;
  showTimers?: boolean;
  timerStatesMap?: Map<string, StudentTimerDisplayState>;
  quizTimerDurationSeconds?: number;
  resultsTableQuestions?: ResultsTableQuestion[];
  participants?: Participant[];
  maxScore: number;
  activityInstanceId: number;
  isRosteredRoom: boolean;
  isActivityInProgress: boolean;
  /** Used to determine sticky header offset. */
  optionsContainer?: React.MutableRefObject<HTMLDivElement>;
  scoreDisplay: ScoreDisplay;
  setScoreDisplay: (newScoreDisplay: ScoreDisplay) => void;
}

const RowHeight = 36 + 4;

/** Standard cell height plus 1.5rem of top padding. */
const HEADER_HEIGHT = 36 + 18;
const QuestionColumnWidths = {
  [QuestionTypes.FreeResponse]: 174 + 4,
  [QuestionTypes.TrueFalse]: 94 + 4,
  [QuestionTypes.MultipleChoice]: 94 + 4,
};

const Percent = ({
  numerator,
  denominator,
}: {
  numerator: number;
  denominator: number;
}) => {
  if (numerator < 0 || denominator <= 0) {
    return null;
  }
  return <>{calculatePercent({ numerator, denominator })}%</>;
};

const DisplayScore = ({
  currentGrade,
  questionsAnswered,
  maxScore,
  scoreDisplay,
  resultsTableQuestionsLength,
  fractionDigits,
}: {
  maxScore: number;
  resultsTableQuestionsLength: number;
  currentGrade: number;
  questionsAnswered: number;
  scoreDisplay: ScoreDisplay;
  fractionDigits: number;
}) => {
  switch (scoreDisplay) {
    case ScoreDisplay.ScorePercent:
      return <Percent numerator={currentGrade} denominator={maxScore} />;
    case ScoreDisplay.ProgressPercent:
      return (
        <Percent
          numerator={questionsAnswered}
          denominator={resultsTableQuestionsLength}
        />
      );
    case ScoreDisplay.ScoreNumber:
      return <>{currentGrade.toFixed(fractionDigits)}</>;
    default:
      return null;
  }
};

/**
 * Given an array of participants and an array of questions answered,
 * renders a table with the results of the activity. Used for live
 * and completed activities.
 */
export function ResultsTable({
  showNames,
  showResults,
  showResponses,
  hasTimers = false,
  showTimers,
  timerStatesMap,
  quizTimerDurationSeconds = 0,
  resultsTableQuestions = [],
  participants = [],
  maxScore,
  isRosteredRoom,
  isActivityInProgress,
  activityInstanceId,
  optionsContainer,
  scoreDisplay,
  setScoreDisplay,
}: Props) {
  const { selectStudentQuestion } = useStudentScoreProvider();
  const { questionScores } = useResultState();
  const { sortDirection, sortedStudentIds, setSortDirection } =
    useStudentSortProvider();
  const sidebarRef = React.useRef(null);
  const responsesRef = React.useRef(null);

  const optionsRect = useRect(optionsContainer, false);
  const sidebarRect = useRect(sidebarRef, false);
  const legacyGutter = useMedia(
    ['(min-width: 960px)', '(min-width: 768px)', '(min-width: 375px)'],
    ['9vw', '3.5vw', '3.25vw'],
    '4.5vw'
  );

  const optionsHeight = optionsRect?.height ?? 0;

  /**
   * Get the table width determined by react-window, and combine it
   * with the width of the participants column to set the full
   * table width on the page body.
   */
  const responsesWidth = responsesRef?.current?.clientWidth || 0;

  React.useEffect(() => {
    if (sidebarRect) {
      document.body.style.width = `calc(${
        sidebarRect.width + Math.ceil(responsesWidth) + 2
      }px + ${legacyGutter})`;
    }

    return () => (document.body.style.width = null);
  }, [sidebarRect, legacyGutter, responsesWidth]);

  const toggleNameColumnSort = () => {
    if (sortDirection === Direction.Ascending) {
      setSortDirection(Direction.Descending);
    } else {
      setSortDirection(Direction.Ascending);
    }
  };

  const sortedParticipants = React.useMemo(
    () =>
      sortItemsByReferenceOrder({
        itemsToSort: participants,
        sortProperty: 'id',
        referenceSortOrder: sortedStudentIds,
      }),
    [participants, sortedStudentIds]
  );

  const getColumnWidth = (index) => {
    const question = resultsTableQuestions[index];

    return QuestionColumnWidths[question.type];
  };

  const renderHeaderCell = ({ index, style }) => {
    const question = resultsTableQuestions[index];

    return (
      <div
        key={`header-${question.questionId}`}
        data-testid={`${
          isActivityInProgress ? 'live' : 'finished'
        }-header-question-${index}`}
        style={{ ...style, height: HEADER_HEIGHT }}
      >
        <div className="pt-6 pl-4px">
          <ResultsQuestionCell
            questionType={question.type}
            order={question.order}
            activityInstanceId={activityInstanceId}
          />
        </div>
      </div>
    );
  };

  const fractionDigits = Math.max(
    ...participants
      .map((participant) => participant.currentGrade)
      .map(countDecimalPlaces)
  );

  const renderCell = ({ columnIndex, rowIndex, style }) => {
    const question = resultsTableQuestions[columnIndex];

    // Footer cell
    if (rowIndex === sortedParticipants.length) {
      return (
        <div
          className="pt-4px pl-4px"
          key={`footer-${question.questionId}`}
          style={style}
        >
          <ResultsFooterCell>
            {question.gradingWeight > 0 && question.totalParticipants > 0 && (
              <Percent
                numerator={question.totalGrades}
                denominator={
                  question.totalParticipants * question.gradingWeight
                }
              />
            )}
          </ResultsFooterCell>
        </div>
      );
    }

    // Content cell
    const participant = sortedParticipants[rowIndex];
    if (participant) {
      const responses = participant.responsesByQuestion[question.questionId];
      let response = responses.length
        ? sortItemsByProperty({
            items: responses,
            property: 'id',
            sortDirection: Direction.Descending,
          })[0]
        : null;
      if (!response) {
        response = {
          id: Date.now(), // This is just a temp ID, once you refresh it fixes things.
          isCorrect: undefined,
          answerText: '',
          hidden: false,
        } as StudentResponse;
      }
      const studentScore = findStudentScore({
        studentId: Number(participant.id),
        questionId: question.questionId,
        questionScores,
      });
      const questionScore = questionScores[question.questionId];
      const gradingWeight =
        questionScore?.gradingWeight ?? question.gradingWeight;

      return (
        <div
          className="pt-4px pl-4px"
          key={`${response.questionId}-${response.userUuid}`}
          style={style}
        >
          <ResultsResponseCell
            response={response}
            studentScore={studentScore}
            gradingWeight={gradingWeight}
            canEditResponse={
              question.type === QuestionTypes.FreeResponse &&
              response.isCorrect !== undefined &&
              !isActivityInProgress
            }
            onSelectResponse={() =>
              selectStudentQuestion({
                studentId: Number(participant.id),
                questionId: question.questionId,
              })
            }
            showResults={showResults}
            showResponses={showResponses}
            isShortAnswer={question.type === QuestionTypes.FreeResponse}
          />
        </div>
      );
    } else {
      console.error(`not found row ${rowIndex} col ${columnIndex}`);
      return <div style={style}></div>;
    }
  };

  // eslint-disable-next-line react/display-name
  const gridInnerElement = React.forwardRef<
    HTMLDivElement,
    { style: React.CSSProperties }
  >(({ style, ...rest }, ref) => (
    <div
      ref={ref}
      style={{
        ...style,
        width: `calc(${style.width}px + 4px)`,
      }}
      {...rest}
    />
  ));

  return (
    <div className="legacy-container">
      <div className="grid grid-rows-1">
        <div className="relative col-start-1 row-span-full">
          <div
            className="sticky left-0 h-full bg-white"
            style={{
              zIndex: 20,
              width: legacyGutter,
              marginLeft: `calc(-${legacyGutter})`,
            }}
          />
          <div className="absolute inset-0">
            <div
              id="sidebar"
              className="sticky top-0 z-20 pt-6 bg-white w-100 md:w-120"
              style={{
                height: HEADER_HEIGHT,
                top: optionsHeight,
                left: legacyGutter,
              }}
              ref={sidebarRef}
            >
              <ResultsParticipantHeader
                onNamePress={toggleNameColumnSort}
                sortDirection={sortDirection}
                setScoreDisplay={setScoreDisplay}
                scoreDisplay={scoreDisplay}
              />
            </div>

            {sortedParticipants.map((participant) => {
              return (
                <div
                  className="sticky z-10 flex pt-4px bg-white w-100 md:w-120"
                  key={participant.id}
                  style={{ height: RowHeight, left: legacyGutter }}
                >
                  <ResultsParticipantCell
                    showName={showNames}
                    isRosteredRoom={isRosteredRoom}
                    isActivityInProgress={isActivityInProgress}
                    present={participant.here}
                    finished={participant.finished}
                    hasTimers={hasTimers}
                    showTimers={showTimers}
                    timerState={timerStatesMap?.get(participant.uuid)}
                    quizTimerDurationSeconds={quizTimerDurationSeconds}
                    participantName={participant.name}
                    displayScore={
                      <DisplayScore
                        maxScore={maxScore}
                        currentGrade={participant.currentGrade}
                        questionsAnswered={participant.questionsAnswered}
                        resultsTableQuestionsLength={
                          resultsTableQuestions.length
                        }
                        fractionDigits={fractionDigits}
                        scoreDisplay={scoreDisplay}
                      />
                    }
                  >
                    {participant.name}
                  </ResultsParticipantCell>
                </div>
              );
            })}

            <div
              className="sticky z-20 pt-4px bg-white w-100 md:w-120"
              style={{ height: RowHeight, left: legacyGutter }}
            >
              <ResultsFooterHeader>{participants.length}</ResultsFooterHeader>
            </div>
          </div>
        </div>

        <div
          className="col-start-1 row-span-full ml-100 md:ml-120"
          ref={responsesRef}
        >
          <ReactWindowScroller>
            {({ ref, outerRef, style, onScroll }) => (
              <VariableSizeList
                className="z-10 bg-white sticky"
                ref={ref}
                outerRef={outerRef}
                style={{
                  ...style,
                  position: 'sticky',
                  top: optionsHeight,
                  display: null,
                  height: HEADER_HEIGHT,
                  overflow: 'hidden',
                }}
                onScroll={onScroll}
                height={HEADER_HEIGHT}
                width={640000}
                innerElementType={gridInnerElement}
                itemCount={resultsTableQuestions.length}
                itemSize={getColumnWidth}
                layout="horizontal"
              >
                {renderHeaderCell}
              </VariableSizeList>
            )}
          </ReactWindowScroller>

          <ReactWindowScroller isGrid>
            {({ ref, outerRef, style, onScroll }) => (
              <VariableSizeGrid
                ref={ref}
                outerRef={outerRef}
                style={{ ...style, display: null }}
                onScroll={onScroll}
                width={window.innerWidth}
                height={window.innerHeight}
                innerElementType={gridInnerElement}
                columnCount={resultsTableQuestions.length}
                rowCount={sortedParticipants.length + 1}
                columnWidth={getColumnWidth}
                rowHeight={() => RowHeight}
              >
                {renderCell}
              </VariableSizeGrid>
            )}
          </ReactWindowScroller>
        </div>
      </div>
    </div>
  );
}
